// ════════════════════════════════════════════════════════════════════════════
// SUPABASE CLIENT — accounts (Discord OAuth) + cloud save data.
// Loads before game.js (same convention as theme.js/sfx.js/network.js — see
// index.html) so window.Supa exists for game.js's top-level calls. The
// publishable key below is safe to ship client-side: Row Level Security on
// every table is what actually protects player data, not secrecy of this
// key (same trust model as a Firebase client config or a Stripe publishable
// key). Never put the Supabase *secret* key here — that one bypasses RLS.
// ════════════════════════════════════════════════════════════════════════════
(function () {
  const SUPABASE_URL = 'https://qbogyczrrsdehwbgjcdc.supabase.co';
  const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_Ikvb-8In0M0T97gzw1y2Sg_z8Jn6pMP';

  if (!window.supabase || typeof window.supabase.createClient !== 'function') {
    console.error('[supabase] supabase-js failed to load — check the CDN <script> tag in index.html.');
    window.Supa = null;
    return;
  }

  const client = window.supabase.createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

  // Mirrors the SDK's own session state locally so synchronous callers
  // (e.g. rendering the Edit Profile modal) don't all need to be async.
  // Updated by onAuthStateChange, which supabase-js fires immediately on
  // subscribe with whatever session already exists (including right after
  // bouncing back from the Discord OAuth redirect), so this is populated
  // before the first real UI render gets a chance to ask for it.
  let currentSession = null;
  const sessionListeners = [];

  client.auth.onAuthStateChange((_event, session) => {
    currentSession = session;
    sessionListeners.forEach(cb => {
      try { cb(session); } catch (err) { console.error('[supabase] onSessionChange listener threw', err); }
    });
  });

  // Sends the player to Discord's consent screen, then back to this exact
  // page (origin + path, query/hash stripped) once they approve — Supabase
  // handles exchanging the returned code for a session automatically and
  // onAuthStateChange above picks it up.
  function signInWithDiscord() {
    return client.auth.signInWithOAuth({
      provider: 'discord',
      options: { redirectTo: window.location.origin + window.location.pathname },
    });
  }

  function signOut() {
    return client.auth.signOut();
  }

  function getSession() {
    return currentSession;
  }

  // cb(session) — session is null when signed out. Called going forward
  // only; callers that need the CURRENT state immediately should also call
  // getSession() themselves (e.g. when first building a modal's HTML).
  function onSessionChange(cb) {
    sessionListeners.push(cb);
  }

  // ── Profile cloud save (public.profiles) ────────────────────────────────
  // profiles is keyed by `id` (= auth.users.id, not `user_id` — the actual
  // deployed table's column name; see the note left in supabase/schema.sql's
  // profiles section, which still describes an earlier `user_id`/`display_name`/
  // `data` jsonb design that was never applied to production). One row per
  // account, auto-created at signup; this only ever reads/writes `username`
  // and `avatar` on that existing row. Same { ok, ... }-never-throws
  // convention as the deck functions below, so a missing/misconfigured
  // column degrades to local-only profile storage instead of crashing —
  // see loadProfileData()/saveProfileData() in game.js for the localStorage
  // side this backs up to the cloud.
  async function loadCloudProfile() {
    const session = getSession();
    if (!session) return { ok: false, profile: null, error: 'not signed in' };
    const { data, error } = await client
      .from('profiles')
      .select('username, avatar')
      .eq('id', session.user.id)
      .maybeSingle();
    if (error) {
      console.error('[supabase] loadCloudProfile failed', error);
      return { ok: false, profile: null, error };
    }
    return {
      ok: true,
      profile: data ? { username: data.username || '', avatar: data.avatar || '' } : null,
    };
  }

  async function saveCloudProfile({ username, avatar }) {
    const session = getSession();
    if (!session) return { ok: false, error: 'not signed in' };
    const { error } = await client
      .from('profiles')
      .update({ username: username || null, avatar: avatar || null })
      .eq('id', session.user.id);
    if (error) console.error('[supabase] saveCloudProfile failed', error);
    return { ok: !error, error };
  }

  // ── Deck cloud save (public.decks — see supabase/schema.sql) ───────────────
  // Row shape: { user_id, name, deck jsonb, created_at, updated_at }. `deck`
  // holds { main: [...], sideboard: [...] } — both of game.js's buildDeckJson()/
  // buildSideboardJson() arrays packed into the one existing jsonb column
  // (rather than adding a second `sideboard` column) so this works against
  // the schema exactly as already committed, with no separate migration step
  // required before this ships. RLS (auth.uid() = user_id) is the only access
  // control — every call here implicitly scopes to the signed-in user via
  // the client's own auth token, but each function also early-returns if
  // there's no session, so a caller can never accidentally read/write without
  // one slipping through.
  //
  // Every method resolves to { ok, error } (write) or { ok, decks, error }
  // (read) rather than throwing — a missing/misconfigured `decks` table (e.g.
  // schema.sql not yet applied to this project) is a real possibility this
  // feature needs to degrade against, not crash on. Callers fall back to
  // localStorage-only behavior when ok is false.
  async function listDecks() {
    const session = getSession();
    if (!session) return { ok: false, decks: [], error: 'not signed in' };
    const { data, error } = await client
      .from('decks')
      .select('name, deck, updated_at')
      .eq('user_id', session.user.id);
    if (error) {
      console.error('[supabase] listDecks failed', error);
      return { ok: false, decks: [], error };
    }
    return {
      ok: true,
      decks: (data || []).map(row => ({
        name: row.name,
        main: Array.isArray(row.deck?.main) ? row.deck.main : [],
        sideboard: Array.isArray(row.deck?.sideboard) ? row.deck.sideboard : [],
        updatedAt: row.updated_at,
      })),
    };
  }

  async function saveDeck(name, mainArr, sideboardArr) {
    const session = getSession();
    if (!session) return { ok: false, error: 'not signed in' };
    const { error } = await client
      .from('decks')
      .upsert(
        { user_id: session.user.id, name, deck: { main: mainArr || [], sideboard: sideboardArr || [] } },
        { onConflict: 'user_id,name' }
      );
    if (error) console.error('[supabase] saveDeck failed', error);
    return { ok: !error, error };
  }

  async function deleteDeck(name) {
    const session = getSession();
    if (!session) return { ok: false, error: 'not signed in' };
    const { error } = await client
      .from('decks')
      .delete()
      .eq('user_id', session.user.id)
      .eq('name', name);
    if (error) console.error('[supabase] deleteDeck failed', error);
    return { ok: !error, error };
  }

  window.Supa = {
    client,
    signInWithDiscord,
    signOut,
    getSession,
    onSessionChange,
    loadCloudProfile,
    saveCloudProfile,
    listDecks,
    saveDeck,
    deleteDeck,
  };
})();
